/**
 * Points Service - Frontend service for FMA points system
 * Handles all point-awarding operations for Elite organizations
 * 
 * Point Rules:
 * - Training Video Upload: 150 points (max 3 per 24 hours)
 * - Daily Login: 25 points (max 1 per day)
 * - Profile Completion 100%: 500 points (one-time)
 * - Referral: 250 points (future implementation)
 */

import { supabase } from '@/lib/supabase';

export interface PointAwardResponse {
  success: boolean;
  points_awarded?: number;
  total_points?: number;
  point_transaction_id?: string;
  reason?: string;
  error?: string;
  limit_reached?: boolean;
}

export interface PointsHistory {
  id: string;
  reason: string;
  points: number;
  occurred_at: string;
  created_by: string | null;
}

export interface PointsTotals {
  organization_id: string;
  total_points: number;
}

/**
 * Award points for training video upload (150 points, max 3 per 24 hours)
 */
export async function awardTrainingVideoPoints(
  organizationId: string
): Promise<PointAwardResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('award-points', {
      body: {
        organization_id: organizationId,
        reason: 'training_video_upload',
      },
    });

    if (error) {

      return {
        success: false,
        error: error.message || 'Failed to award points',
      };
    }

    return data as PointAwardResponse;
  } catch (error) {

    return {
      success: false,
      error: 'An unexpected error occurred',
    };
  }
}

/**
 * Award points for daily login (25 points, max 1 per day)
 */
export async function awardDailyLoginPoints(
  organizationId: string
): Promise<PointAwardResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('award-points', {
      body: {
        organization_id: organizationId,
        reason: 'daily_login',
      },
    });

    if (error) {

      return {
        success: false,
        error: error.message || 'Failed to award points',
      };
    }

    return data as PointAwardResponse;
  } catch (error) {

    return {
      success: false,
      error: 'An unexpected error occurred',
    };
  }
}

/**
 * Award points for profile completion (500 points, one-time only)
 */
export async function awardProfileCompletionPoints(
  organizationId: string
): Promise<PointAwardResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('award-points', {
      body: {
        organization_id: organizationId,
        reason: 'profile_completion_100',
      },
    });

    if (error) {
      // Don't log errors for "already awarded" - this is expected behavior
      const isAlreadyAwarded = error.message?.includes('already awarded');
      if (!isAlreadyAwarded) {

      }
      return {
        success: false,
        error: error.message || 'Failed to award points',
      };
    }

    return data as PointAwardResponse;
  } catch (error) {

    return {
      success: false,
      error: 'An unexpected error occurred',
    };
  }
}

/**
 * Calculate current profile completion percentage
 */
export async function checkProfileCompletion(
  organizationId: string
): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('calculate_profile_completion', {
      org_id: organizationId,
    });

    if (error) {

      return 0;
    }

    return data as number;
  } catch (error) {

    return 0;
  }
}

/**
 * Get total points for an organization
 */
export async function getTotalPoints(
  organizationId: string
): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('org_points_totals')
      .select('total_points')
      .eq('organization_id', organizationId)
      .single();

    if (error) {
      // Organization might not have any points yet
      if (error.code === 'PGRST116') {
        return 0;
      }

      return 0;
    }

    return data?.total_points || 0;
  } catch (error) {

    return 0;
  }
}

/**
 * Get points history for an organization
 */
export async function getPointsHistory(
  organizationId: string,
  limit: number = 50
): Promise<PointsHistory[]> {
  try {
    const { data, error } = await supabase
      .from('fma_points')
      .select('id, reason, points, occurred_at, created_by')
      .eq('subject_id', organizationId)
      .eq('subject_type', 'org')
      .order('occurred_at', { ascending: false })
      .limit(limit);

    if (error) {

      return [];
    }

    return (data || []) as PointsHistory[];
  } catch (error) {

    return [];
  }
}

/**
 * Check if organization can earn training video points (under 3/day limit)
 */
export async function canAwardTrainingVideoPoints(
  organizationId: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc(
      'can_award_training_video_points',
      {
        org_id: organizationId,
      }
    );

    if (error) {

      return false;
    }

    return data as boolean;
  } catch (error) {

    return false;
  }
}

/**
 * Get friendly reason text for display
 */
export function getReasonDisplayText(reason: string): string {
  const reasonMap: Record<string, string> = {
    training_video_upload: 'Training Video Upload',
    daily_login: 'Daily Login Bonus',
    profile_completion_100: 'Profile Completed',
    referral: 'Agency Referral',
  };

  return reasonMap[reason] || reason;
}

/**
 * Get emoji for point reason
 */
export function getReasonEmoji(reason: string): string {
  const emojiMap: Record<string, string> = {
    training_video_upload: '🎥',
    daily_login: '✨',
    profile_completion_100: '🏆',
    referral: '🤝',
  };

  return emojiMap[reason] || '⭐';
}

export const pointsService = {
  awardTrainingVideoPoints,
  awardDailyLoginPoints,
  awardProfileCompletionPoints,
  checkProfileCompletion,
  getTotalPoints,
  getPointsHistory,
  canAwardTrainingVideoPoints,
  getReasonDisplayText,
  getReasonEmoji,
};
